import { useEffect, useRef, useState } from 'react';

interface ServiceItem {
  id: string;
  icon: string;
  title: string;
  description: string;
  iconBg?: string;
  color?: string;
  image?: string;
  order?: number;
}

interface ServicesProps {
  items?: ServiceItem[];
  loading?: boolean;
}

export default function Services({ items = [], loading = false }: ServicesProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Tách service items và section image từ props
  const sectionImageItem = items.find((item) => 
    item.title === '_SECTION_IMAGE' || (item.order !== undefined && item.order < 0)
  );
  const services = items.filter((item) => 
    item.title !== '_SECTION_IMAGE' && (item.order === undefined || item.order >= 0)
  );
  const sectionImage = sectionImageItem?.image || '';

  return (
    <section ref={sectionRef} className="py-24 bg-white relative overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Visual */}
          {sectionImage && (
            <div className={`relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-[#e2e8f0] bg-white shadow-xl group hover:shadow-2xl transition-all duration-500">
                <img 
                  src={sectionImage}
                  alt="Code Review Process"
                  className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-700"
                />
              
                {/* Floating badges */}
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 border border-[#e2e8f0] shadow-lg animate-[float_3s_ease-in-out_infinite]">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-sm font-semibold text-[#1a202c]">Scanning Active</span>
                  </div>
                </div>
              
                <div className="absolute bottom-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 border border-[#e2e8f0] shadow-lg animate-[float_3s_ease-in-out_infinite]" style={{ animationDelay: '1.5s' }}>
                  <div className="text-xs text-[#4a5568] mb-1">Vulnerabilities Found</div>
                  <div className="text-2xl font-bold text-[#06b6d4]">0 Critical</div>
                </div>
              </div>

              {/* Decorative elements */}
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-[#06b6d4]/10 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#06b6d4]/5 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          )}

          {/* Right: Content */}
          <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            <div className="inline-block px-4 py-2 bg-[#06b6d4]/10 rounded-full mb-6">
              <span className="text-[#06b6d4] text-sm font-semibold uppercase tracking-wider">Dịch vụ toàn diện</span>
            </div>
            
            <h2 className="font-['Inter',sans-serif] text-4xl md:text-5xl font-bold text-[#1a202c] mb-6">
              Dịch vụ bao gồm
            </h2>
            
            <p className="text-lg text-[#4a5568] mb-10 leading-relaxed">
              Quy trình đánh giá bảo mật chuyên sâu với phương pháp tiếp cận toàn diện từ code đến infrastructure.
            </p>

            {/* Services List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block w-16 h-16 border-4 border-[#06b6d4] border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Đang tải...</p>
              </div>
            ) : services.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">Chưa có nội dung</p>
              </div>
            ) : (
              <div className="space-y-6">
                {services.map((service, index) => (
                  <div 
                    key={service.id}
                    className={`group flex gap-4 p-5 rounded-xl border-2 border-transparent hover:border-[#06b6d4] hover:bg-white hover:shadow-lg transition-all duration-300 ${
                      isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                    }`}
                    style={{ 
                      transitionDelay: isVisible ? `${0.4 + index * 0.1}s` : '0s'
                    }}
                  >
                    <div className={`flex-shrink-0 w-12 h-12 rounded-lg ${service.iconBg || 'bg-gradient-to-br from-[#06b6d4]/10 to-[#0891b2]/10'} flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                      <i className={`${service.icon} text-2xl ${service.color || 'text-[#06b6d4]'}`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a202c] mb-2 group-hover:text-[#06b6d4] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-sm text-[#4a5568] leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}